import * as StepTypes from "./steps.types";

// ─── Data ─────────────────────────────────────────────────────────────────────

export const PROCESS_STEPS: StepTypes.ProcessStep[] = [
    {
        id: "get-ready",
        number: 1,
        icon: "/icons/thumbs-up.svg",
        phase: "Get Ready",
        title: "Get Ready — and Be In Control",
        subtitle: "Step One",
        description:
            "HomeShark guides you through every stage of buying a home — how much you can truly afford, what closing costs to expect, what documents your lender needs, and how to find the right professionals. Bait floats along. Sharks are in control.",
        accentColor: "#004cbf",
        resources: [
            {
                id: "affordability-tool",
                type: "tool",
                title: "Affordability Calculator",
                description:
                    "Know exactly what price range you can afford — not just what a lender approves. Factors in taxes, insurance, HOA, and all closing costs.",
                label: "Calculator",
                meta: "Free tool",
                href: "/tools/affordability",
                isReal: true,
                isFree: true,
            },
            {
                id: "readiness-quiz-s1",
                type: "tool",
                title: "Financial Readiness Quiz",
                description:
                    "Find out if your credit, savings, and income are in the right shape before you apply for a loan or start touring homes.",
                label: "Quiz",
                meta: "Subscription · 5 min",
                href: "/tools/readiness-quiz",
                isReal: true,
                isFree: false,
            },
            {
                id: "costs-and-docs",
                type: "article",
                title: "The Real Cost of Buying a Home — and What Your Lender Will Ask For",
                description:
                    "Two essentials in one: a plain-English breakdown of every cost you'll face at closing, plus a complete list of the documents your lender will require before you get approved.",
                label: "Article",
                meta: "Free · 12 min read",
                href: "/articles/real-costs-and-lender-docs",
                isReal: false,
                isFree: true,
            },
            {
                id: "buyer-dashboard-intro",
                type: "tool",
                title: "Buyer Dashboard",
                description:
                    "Your custom dashboard shows your next step at every point in the process — from first search to signed contract. See where you are and what to do next.",
                label: "Tool",
                meta: "Subscription",
                href: "/dashboard",
                isReal: true,
                isFree: false,
            },
            {
                id: "find-professionals-early",
                type: "article",
                title: "Why You Need a Lender, Inspector & Attorney Before You Find a House",
                description:
                    "Most buyers line up professionals after they find a home — that's too late. Here's why locking in your team before you search protects you at every stage.",
                label: "Article",
                meta: "Free · 8 min read",
                href: "/articles/build-your-team",
                isReal: false,
                isFree: true,
            },
            {
                id: "dpa-georgia",
                type: "article",
                title: "Georgia Down Payment Assistance Programs",
                description:
                    "A curated directory of Georgia DPA programs — state, county, and nonprofit — with eligibility requirements, income limits, and direct links to apply.",
                label: "Resource Directory",
                meta: "Free · External links",
                href: "/guides/ga-down-payment-assistance",
                // externalHref: "https://www.example.com",
                isReal: true,
                isFree: true,
            },
        ],
    },
    {
        id: "search",
        number: 2,
        icon: "/icons/search-gray.svg",
        phase: "Search Homes",
        title: "Search the Full MLS — Unfiltered",
        subtitle: "Step Two",
        description:
            "Browse the same listings every agent sees — every home on the Georgia MLS. Not a curated subset, not homes from agents who paid to be featured. This section gives you the knowledge and tools to search smarter, prioritize what actually matters, and move fast when the right home appears.",
        accentColor: "#004cbf",
        resources: [
            {
                id: "needs-tool",
                type: "tool",
                title: "HomeShark Needs Tool",
                description:
                    "Clarify your must-haves vs. nice-to-haves before diving into the MLS — so your search stays focused.",
                label: "Interactive Tool",
                meta: "Subscription",
                href: "/tools/needs",
                isReal: false,
                isFree: false,
            },
            {
                id: "affordability-calc",
                type: "tool",
                title: "Affordability Calculator",
                description:
                    "Know your real price range — not just what a lender approves. Factor in taxes, insurance, HOA, and closing costs.",
                label: "Calculator",
                meta: "Free tool",
                href: "/tools/affordability",
                isReal: true,
                isFree: true,
            },
            {
                id: "how-to-search-video",
                type: "video",
                title: "How to Create a Home Search on HomeShark",
                description:
                    "A step-by-step walkthrough of setting up your MLS search — filters, saved searches, and real-time alerts — so you never miss a listing that fits.",
                label: "Video",
                meta: "Free",
                href: "/learn/how-to-create-home-search",
                isReal: false,
                isFree: true,
            },
            {
                id: "search-guide",
                type: "article",
                title: "How to Read an MLS Listing (Without a Real Estate License)",
                description:
                    "What do DOM, contingent, and active under contract actually mean? This guide decodes listing status and data.",
                label: "Guide",
                meta: "7 min read",
                href: "/articles/glossary-page",
                isReal: false,
            },
            {
                id: "school-district-guide",
                type: "article",
                title: "How to Research School Districts Like a Pro",
                description:
                    "Whether you have kids or not, school districts drive home values. Here's how to evaluate them objectively.",
                label: "Article",
                meta: "5 min read",
                href: "/articles/georgia-schools-guide",
                isReal: false,
            },
        ],
    },
    {
        id: "tour",
        number: 3,
        icon: "/icons/home-gray.svg",
        phase: "Tour Homes",
        title: "See Homes Privately — On Your Terms",
        subtitle: "Step Three",
        description:
            "HomeShark gives you three ways to tour a home — one is free, the others give you privacy without locking you into a long-term buyer's agent agreement just to walk through a door. This section prepares you to tour with purpose: what to look for, what questions to ask, and how to evaluate a home beyond the staging.",
        accentColor: "#0062e8",
        resources: [
            {
                id: "see-it-like-a-shark",
                type: "series" as StepTypes.ResourceType,
                title: "See It Like a Shark",
                description:
                    "A free 4–5 part video series that walks you through a showing the way a sharp buyer does it — starting before you ever schedule the visit. Covers what to research beforehand, what to look for room by room, what to ask on-site, and how to evaluate what you saw once you get home.",
                label: "Free Video Series",
                meta: "Free · 4–5 videos",
                href: "/learn/see-it-like-a-shark",
                isReal: false,
                isFree: true,
            },
            {
                id: "showing-video",
                type: "video",
                title: "How HomeShark Showings Work",
                description:
                    "A quick walkthrough of all three showing options — open houses, listing agent showings, and on-demand showings.",
                label: "Video",
                meta: "4 min",
                href: "/learn/showings-explainer",
                isReal: false,
            },
            {
                id: "tour-checklist",
                type: "checklist",
                title: "Home Tour Checklist (Bring This)",
                description:
                    "47 things to check during a showing — from foundation cracks to water pressure to cell signal in every room.",
                label: "Checklist",
                meta: "PDF · Free",
                href: "/checklists/home-tour",
                isReal: false,
                isFree: true,
            },
            {
                id: "red-flags-article",
                type: "article",
                title: "Red Flags to Spot at a Showing (That Agents Often Miss)",
                description:
                    "Freshly painted basement walls, new carpet over hardwood, and 9 other signs a seller is hiding something.",
                label: "Article",
                meta: "9 min read",
                href: "/articles/showing-red-flags",
                isReal: false,
            },
            {
                id: "questions-article",
                type: "article",
                title: "Questions to Ask at Every Showing",
                description:
                    "What to ask the listing agent, what not to say, and how to get honest answers about the home's history.",
                label: "Guide",
                meta: "6 min read",
                href: "/guides/questions-to-ask",
                isReal: false,
            },
            {
                id: "neighborhood-guide",
                type: "article",
                title: "How to Evaluate a Neighborhood in 30 Minutes",
                description:
                    "Drive patterns, noise windows, walkability, and the one thing most buyers forget to check before making an offer.",
                label: "Article",
                meta: "5 min read",
                href: "/learn/evaluate-neighborhood",
                isReal: false,
            },
            {
                id: "sellers-disclosure-guide",
                type: "article",
                title: "How to Read a Georgia Seller's Disclosure",
                description:
                    "Every section of the GAR F301 explained — what the seller is telling you, what red flags to look for, and how to use it before you make an offer.",
                label: "Guide",
                meta: "12 min read",
                href: "/guides/sellers-disclosure",
                isReal: true,
            },
        ],
    },
    {
        id: "offer",
        number: 4,
        icon: "/icons/pencil.svg",
        phase: "Write an Offer",
        title: "Write an Offer — With Zero Confusion",
        subtitle: "Step Four",
        description:
            "HomeShark's Contract Builder walks you through every term in the Georgia purchase agreement in plain English. You understand what you're signing and why each clause matters before you sign it. This section builds your confidence: offer strategy, negotiation, and exactly how the GA-standard contract protects you.",
        accentColor: "#00b4f1",
        resources: [
            {
                id: "offer-builder",
                type: "tool",
                title: "Contract Builder",
                description:
                    "Answer guided questions, we populate the official Georgia Association of Realtors form. Reviewed by a licensed agent before submission.",
                label: "Tool",
                meta: "Subscription",
                href: "/tools/offer-builder",
                isReal: false,
                isFree: false,
            },
            {
                id: "decisions-before-writing-offer",
                type: "article",
                title: "Every Decision You Need to Make Before Writing an Offer",
                description:
                    "Purchase price, due diligence period, earnest money, closing date, financing contingency, appraisal gap, special stipulations — every contract term explained in plain English so you walk in prepared, whether you're writing with HomeShark or an agent.",
                label: "Guide",
                meta: "Free · 12 min read",
                href: "/articles/decisions-before-writing-offer",
                isReal: false,
                isFree: true,
            },
            {
                id: "offer-terms-checklist",
                type: "checklist",
                title: "Offer Preparation Checklist",
                description:
                    "A fillable checklist covering every decision you'll need to make before writing: price, due diligence days, earnest money amount, contingencies, closing timeline, and special stipulations. Fill it out before you sit down to write — with HomeShark or anyone else.",
                label: "Checklist",
                meta: "PDF · Free",
                href: "/checklists/pre-offer-worksheet",
                isReal: false,
                isFree: true,
            },
            {
                id: "sharkcomp-video",
                type: "video",
                title: "How to Read Your SharkComp Like an Agent",
                description:
                    "A step-by-step walkthrough of how to use the SharkComp tool — HomeShark pulls comparable sales based on your home's parameters, and this video shows you how to read them the way an agent would to determine what an offer should look like.",
                label: "Video",
                meta: "Subscription",
                href: "/learn/how-to-read-sharkcomp",
                isReal: false,
                isFree: false,
            },
            {
                id: "contingencies-article",
                type: "article",
                title: "Georgia Contract Contingencies, Explained",
                description:
                    "Financing, inspection, and appraisal contingencies — what they protect, when to waive them, and the risk of each.",
                label: "Article",
                meta: "Free · 8 min read",
                href: "/articles/contingencies",
                isReal: true,
                isFree: true,
            },
            {
                id: "negotiation-article",
                type: "article",
                title: "Counter-Offers: How to Negotiate Without an Agent",
                description:
                    "What moves to make, what to let go, and how to keep a deal alive when a seller counters your offer.",
                label: "Guide",
                meta: "Free · 7 min read",
                href: "/guides/counter-offer-guide",
                isReal: false,
                isFree: true,
            },
        ],
    },
    {
        id: "close",
        number: 5,
        icon: "/icons/flag-square.svg",
        phase: "Inspection to Closing",
        title: "Inspection to Closing — Guided Every Step",
        subtitle: "Step Five",
        description:
            "Getting under contract is just the beginning. The period between accepted offer and closing is where most buyers feel lost — inspections, lender milestones, appraisals, due diligence, and the final walkthrough. Your Buyer Dashboard tracks every milestone and surfaces your next action. This section prepares you for each stage so nothing falls through the cracks.",
        accentColor: "#26c8f5",
        resources: [
            {
                id: "buyer-dashboard",
                type: "tool",
                title: "Home Capital Plan",
                description:
                    "Map your roof, HVAC, water heater, and appliances - plus any renovations you're planning - against a 10-year timeline, and see exactly how much to save each month after closing.",
                label: "Tool",
                meta: "Subscription",
                href: "/dashboard/tools/home-capital-plan",
                isReal: true,
                isFree: false,
            },
            {
                id: "inspection-guide",
                type: "article",
                title: "How to Use Your Inspection Report in Negotiations",
                description:
                    "Not every issue is a dealbreaker. Learn how to triage inspection findings and what to ask the seller to fix vs. accept.",
                label: "Guide",
                meta: "9 min read",
                href: "/guides/inspection-guide",
                isReal: false,
            },
            {
                id: "ga-closing-costs",
                type: "article",
                title: "Georgia Closing Costs: The Complete Breakdown",
                description:
                    "Every fee on your closing disclosure — lender fees, title, transfer tax, prepaid escrow — explained line by line.",
                label: "Article",
                meta: "11 min read",
                href: "/articles/ga-closing-costs",
                isReal: false,
            },
            {
                id: "closing-checklist",
                type: "checklist",
                title: "Closing Day Checklist",
                description:
                    "What to bring, what to expect, and what to verify before you sign. Final walkthrough notes included.",
                label: "Checklist",
                meta: "PDF · Free",
                href: "/checklists/closing-day",
                isReal: false,
                isFree: true,
            },
            {
                id: "appraisal-video",
                type: "video",
                title: "What Happens if the Appraisal Comes in Low?",
                description:
                    "A common situation that derails deals — here's what your options are and how to navigate it with your lender.",
                label: "Video",
                meta: "5 min",
                href: "/learn/low-appraisal",
                isReal: false,
            },
            {
                id: "lender-milestones",
                type: "article",
                title: "Lender Milestones After Acceptance: A Timeline",
                description:
                    "Conditional approval, appraisal order, clear to close — what each lender milestone means and how long each takes in Georgia.",
                label: "Guide",
                meta: "6 min read",
                href: "/guides/lender-milestones",
                isReal: false,
            },
        ],
    },
];
